import type {
  BankAccount,
  FinancialItem,
  InvestmentCompany,
  RealEstateProperty,
} from '../types';

export type CompanyMetrics = {
  monthlyEBITDA: number;
  annualRevenue: number;
  annualEBITDA: number;
  companyValuation: number;
  myEquityValue: number;
  expectedExitValueThisYear: number;
  expectedReturnThisYear: number;
};

export function computeCompanyMetrics(c: InvestmentCompany): CompanyMetrics {
  /** Placeholders until revenue / P&L live in a dedicated table. */
  const monthlyEBITDA = 0;
  const annualRevenue = 0;
  const annualEBITDA = 0;

  let companyValuation = 0;
  switch (c.valuationMethod) {
    case 'manual':
      companyValuation = c.manualValuation ?? 0;
      break;
    case 'revenueMultiple':
      companyValuation = annualRevenue * (c.revenueMultiple ?? 0);
      break;
    case 'ebitdaMultiple':
      companyValuation = annualEBITDA * (c.ebitdaMultiple ?? 0);
      break;
    default:
      companyValuation = 0;
  }

  const myEquityValue = companyValuation * (c.ownershipPercentage / 100);
  const expectedExitValueThisYear = myEquityValue * (c.exitProbabilityThisYear / 100);
  const expectedReturnThisYear = expectedExitValueThisYear + c.expectedDividendThisYear;

  return {
    monthlyEBITDA,
    annualRevenue,
    annualEBITDA,
    companyValuation,
    myEquityValue,
    expectedExitValueThisYear,
    expectedReturnThisYear,
  };
}

export function totalAssets(items: FinancialItem[]): number {
  return items.filter((i) => i.type === 'asset').reduce((s, i) => s + i.currentValue, 0);
}

export function totalLiabilities(items: FinancialItem[]): number {
  return items.filter((i) => i.type === 'liability').reduce((s, i) => s + i.currentValue, 0);
}

export type FinancialPositionSummary = {
  assets: number;
  liabilities: number;
  net: number;
};

/** Assets, liabilities, and net (assets − liabilities) for a financial item set. */
export function financialPositionSummary(items: FinancialItem[]): FinancialPositionSummary {
  const assets = totalAssets(items);
  const liabilities = totalLiabilities(items);
  return { assets, liabilities, net: assets - liabilities };
}

export function totalInvestmentPortfolio(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => s + computeCompanyMetrics(c).myEquityValue, 0);
}

export function totalTicketSize(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => {
    const t = typeof c.ticketSize === 'number' && Number.isFinite(c.ticketSize) ? c.ticketSize : 0;
    return s + t;
  }, 0);
}

/**
 * Paper + realized value: active holdings use equity value; exits use proceeds when set,
 * otherwise equity as fallback; dead investments count as 0.
 */
export function totalPortfolioCurrentValue(companies: InvestmentCompany[]): number {
  return companies.reduce((sum, c) => {
    const st = c.investmentStatus;
    const status = st === 'exit' || st === 'dead' || st === 'active' ? st : 'active';
    if (status === 'dead') return sum;
    const m = computeCompanyMetrics(c);
    if (status === 'exit') {
      const proceeds =
        typeof c.exitTotalEuros === 'number' && Number.isFinite(c.exitTotalEuros)
          ? c.exitTotalEuros
          : m.myEquityValue;
      return sum + proceeds;
    }
    return sum + m.myEquityValue;
  }, 0);
}

/** Absolute gain/loss vs sum of ticket sizes. */
export function portfolioOverallGain(companies: InvestmentCompany[]): number {
  return totalPortfolioCurrentValue(companies) - totalTicketSize(companies);
}

export function totalCompanyValuation(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => s + computeCompanyMetrics(c).companyValuation, 0);
}

export function totalExpectedExitThisYear(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => s + computeCompanyMetrics(c).expectedExitValueThisYear, 0);
}

export function totalExpectedDividendsThisYear(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => s + c.expectedDividendThisYear, 0);
}

export function totalExpectedReturnThisYear(companies: InvestmentCompany[]): number {
  return companies.reduce((s, c) => s + computeCompanyMetrics(c).expectedReturnThisYear, 0);
}

/** Gross market value across all real-estate holdings (used for allocation). */
export function totalRealEstateValue(properties: RealEstateProperty[]): number {
  return properties.reduce((sum, property) => {
    const value =
      typeof property.currentValue === 'number' && Number.isFinite(property.currentValue)
        ? property.currentValue
        : 0;
    return sum + value;
  }, 0);
}

/** Net real-estate equity: market value minus outstanding mortgage across holdings. */
export function totalRealEstateEquity(properties: RealEstateProperty[]): number {
  return properties.reduce((sum, property) => {
    const value =
      typeof property.currentValue === 'number' && Number.isFinite(property.currentValue)
        ? property.currentValue
        : 0;
    const mortgage =
      typeof property.mortgageBalance === 'number' && Number.isFinite(property.mortgageBalance)
        ? property.mortgageBalance
        : 0;
    return sum + value - mortgage;
  }, 0);
}

/** Outstanding mortgage / loan balance across all real-estate holdings. */
export function totalRealEstateDebt(properties: RealEstateProperty[]): number {
  return properties.reduce((sum, property) => {
    const mortgage =
      typeof property.mortgageBalance === 'number' && Number.isFinite(property.mortgageBalance)
        ? property.mortgageBalance
        : 0;
    return sum + mortgage;
  }, 0);
}

/** Sum EUR balances across connected bank accounts. */
export function totalBankCash(accounts: BankAccount[]): number {
  return accounts.reduce((sum, account) => {
    if (account.currency !== 'EUR') return sum;
    const balance =
      typeof account.balance === 'number' && Number.isFinite(account.balance) ? account.balance : 0;
    return sum + balance;
  }, 0);
}

export function netWorth(
  financialItems: FinancialItem[],
  companies: InvestmentCompany[],
  bankAccounts?: BankAccount[]
): number {
  const portfolio = totalInvestmentPortfolio(companies);
  const assets = totalAssets(financialItems);
  const liabilities = totalLiabilities(financialItems);
  const bankCash = bankAccounts ? totalBankCash(bankAccounts) : 0;
  return portfolio + assets - liabilities + bankCash;
}
